import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import DramaCard from '@/components/base/DramaCard';
import { dramas, genreOptions, regionOptions, yearOptions } from '@/mocks/dramas';

const sortOptions = [
  { key: 'default', label: '默认推荐' },
  { key: 'latest', label: '最新' },
  { key: 'hot', label: '最热' },
];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const genreParam = searchParams.get('genre') || '全部';

  const [genre, setGenre] = useState(genreParam);
  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState('default');
  const [visibleCount, setVisibleCount] = useState(9);

  const filtered = useMemo(() => {
    const list = dramas.filter((d) => {
      const genreMatch = genre === '全部' || d.genres.includes(genre);
      const regionMatch = region === '全部' || d.region === region;
      const yearMatch = year === '全部' || String(d.year) === year;
      return genreMatch && regionMatch && yearMatch;
    });
    if (sort === 'latest') {
      return [...list].sort((a, b) => b.airDate.localeCompare(a.airDate));
    }
    if (sort === 'hot') {
      return [...list].sort((a, b) => b.views - a.views);
    }
    return list;
  }, [genre, region, year, sort]);

  const visible = filtered.slice(0, visibleCount);

  const selectGenre = (value: string) => {
    setGenre(value);
    setVisibleCount(9);
    const next = new URLSearchParams(searchParams);
    if (value === '全部') {
      next.delete('genre');
    } else {
      next.set('genre', value);
    }
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    setGenre('全部');
    setRegion('全部');
    setYear('全部');
    setSort('default');
    setVisibleCount(9);
    setSearchParams({}, { replace: true });
  };

  const hasActiveFilter = genre !== '全部' || region !== '全部' || year !== '全部';

  const FilterGroup = ({
    label,
    options,
    value,
    onChange,
  }: {
    label: string;
    options: string[];
    value: string;
    onChange: (v: string) => void;
  }) => (
    <div className="mb-6">
      <h3 className="text-xs font-semibold text-foreground-400 mb-3 tracking-wide">{label}</h3>
      <div className="flex flex-wrap gap-2">
        {['全部', ...options].map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`rounded-md px-3 py-1.5 text-xs transition-colors cursor-pointer whitespace-nowrap ${
              value === opt
                ? 'bg-primary-600 text-background-50'
                : 'bg-background-900 text-foreground-300 hover:bg-background-800'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-300">短剧分类</span>
        </nav>

        <div className="mb-8 md:mb-10">
          <h1 className="text-2xl md:text-4xl font-bold text-foreground-50 font-heading">短剧分类</h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-400 leading-relaxed">
            按类型、地区与年代多维筛选，快速定位你感兴趣的短篇剧集。共收录 {dramas.length} 部精选作品。
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar filter - desktop */}
          <aside className="hidden lg:block w-60 shrink-0">
            <div className="sticky top-24 rounded-lg border border-background-800 bg-background-900/60 p-5">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-sm font-semibold text-foreground-50">
                  <i className="ri-filter-3-line mr-1.5 text-primary-400" />筛选条件
                </h2>
                {hasActiveFilter && (
                  <button type="button" onClick={resetFilters} className="text-xs text-primary-400 hover:text-primary-300 cursor-pointer">
                    清空
                  </button>
                )}
              </div>
              <FilterGroup label="类型" options={genreOptions} value={genre} onChange={selectGenre} />
              <FilterGroup label="地区" options={regionOptions} value={region} onChange={(v) => { setRegion(v); setVisibleCount(9); }} />
              <FilterGroup label="年代" options={yearOptions.map(String)} value={year} onChange={(v) => { setYear(v); setVisibleCount(9); }} />
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            {/* Mobile filters */}
            <div className="lg:hidden flex flex-wrap gap-2 mb-5">
              <select
                value={genre}
                onChange={(e) => selectGenre(e.target.value)}
                className="flex-1 min-w-[120px] rounded-md bg-background-900 border border-background-700 px-3 py-2 text-sm text-foreground-100 focus:border-primary-500 focus:outline-none"
              >
                {['全部', ...genreOptions].map((o) => <option key={o} value={o}>{o === '全部' ? '全部类型' : o}</option>)}
              </select>
              <select
                value={region}
                onChange={(e) => { setRegion(e.target.value); setVisibleCount(9); }}
                className="flex-1 min-w-[120px] rounded-md bg-background-900 border border-background-700 px-3 py-2 text-sm text-foreground-100 focus:border-primary-500 focus:outline-none"
              >
                {['全部', ...regionOptions].map((o) => <option key={o} value={o}>{o === '全部' ? '全部地区' : o}</option>)}
              </select>
              <select
                value={year}
                onChange={(e) => { setYear(e.target.value); setVisibleCount(9); }}
                className="flex-1 min-w-[120px] rounded-md bg-background-900 border border-background-700 px-3 py-2 text-sm text-foreground-100 focus:border-primary-500 focus:outline-none"
              >
                {['全部', ...yearOptions.map(String)].map((o) => <option key={o} value={o}>{o === '全部' ? '全部年代' : o}</option>)}
              </select>
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <p className="text-sm text-foreground-400">
                共找到 <span className="text-primary-400 font-semibold">{filtered.length}</span> 部短剧
              </p>
              <div className="inline-flex items-center gap-1 rounded-full bg-background-900 p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSort(opt.key)}
                    className={`rounded-full px-3.5 py-1.5 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      sort === opt.key ? 'bg-primary-600 text-background-50 font-medium' : 'text-foreground-300 hover:text-foreground-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {visible.length > 0 ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
                  {visible.map((drama) => (
                    <DramaCard key={drama.id} drama={drama} />
                  ))}
                </div>
                {visibleCount < filtered.length && (
                  <div className="mt-10 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((c) => c + 6)}
                      className="inline-flex items-center gap-2 rounded-md border border-background-600 bg-background-900 px-6 py-2.5 text-sm font-medium text-foreground-100 hover:border-primary-500 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line" />
                      加载更多
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-700 bg-background-900/40 py-20">
                <i className="ri-search-eye-line text-5xl text-foreground-600" />
                <p className="mt-4 text-foreground-300">未找到符合条件的短剧</p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 rounded-md bg-primary-600 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  清空筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}